// NIP-07 browser extension client.
//
// Detects window.nostr (Alby, nos2x, Flamingo). Returns a NostrSigner
// adapter whose shape matches the NIP-46 result so call sites stay
// signer-agnostic.

import {
  NoExtensionError,
  type AuthResult,
  type NostrSigner,
  type SignedNostrEvent,
  type UnsignedNostrEvent,
} from './types.js';

interface Nip07Window {
  nostr?: {
    getPublicKey(): Promise<string>;
    signEvent(event: UnsignedNostrEvent): Promise<SignedNostrEvent>;
    getRelays?(): Promise<Record<string, { read: boolean; write: boolean }>>;
  };
}

function getNip07(): Nip07Window['nostr'] {
  if (typeof window === 'undefined') return undefined;
  return (window as unknown as Nip07Window).nostr;
}

export function hasNip07Extension(): boolean {
  return getNip07() !== undefined;
}

export async function signIn(): Promise<AuthResult> {
  const ext = getNip07();
  if (!ext) {
    throw new NoExtensionError();
  }
  const pubkey = await ext.getPublicKey();
  if (typeof pubkey !== 'string' || pubkey.length === 0) {
    throw new Error('NIP-07 extension returned an empty public key');
  }

  const signer: NostrSigner = {
    type: 'nip07',
    pubkey,
    async signEvent(event: UnsignedNostrEvent): Promise<SignedNostrEvent> {
      const ext2 = getNip07();
      if (!ext2) throw new NoExtensionError();
      const signed = await ext2.signEvent({ ...event, pubkey });
      // Defensive: the extension MUST return the same kind we asked for.
      if (signed.kind !== event.kind) {
        throw new Error(
          `NIP-07 extension returned kind ${signed.kind}, expected ${event.kind}`,
        );
      }
      return signed;
    },
  };

  return { pubkey, signer };
}
